/**
 * Die lange Spur hat Vorfahrt. (17.10.2026, Besitzer-Prinzip)
 *
 * Martin, 17.10. 04:58: *„Wenn eine Morgenmeditation läuft oder eine andere Audiospur gerade am Laufen ist, zum Beispiel
 * auch Essenzspiegel, dann darf diese Spur durch keine andere überdeckt werden oder eine zweite parallel laufen — und es
 * soll auch kein Mikrofon aufgehen, da er sich sonst selbst aufnehmen würde.“*
 *
 * Befund: Die Meditation (NarrationEngine) steht auf der Bühne des Ton-Leitstands, die TTS-Warteschlange (Antworten,
 * Schmied) NICHT — sie wusste von keiner laufenden Spur und sprach darüber. Dazu öffnete das Mikro von selbst (Auto-Mikro
 * nach Antwort, Wach-Wort, Freihand), nahm die Meditation auf und schickte Teile davon als Martins Nachricht.
 *
 * ## Die Regel (EINE Stelle)
 *
 * Eine LANGE SPUR ist, was die Bühne hält UND klingt UND länger als LANGE_DAUER_S dauert (Meditation, Essenz-Spiegel,
 * Biografie/Buch, Datei-Player) — oder Musik (eingebetteter Player, wiedergabe-stand.ts). Solange eine läuft:
 *   a) beginnt keine automatische Vorlese-Stimme: Vesperas Schlange WARTET (tts-queue.ts), die des Schmieds wird verworfen
 *      (Blase bleibt, nur nicht laut);
 *   b) öffnet das Mikro nicht von selbst (`mikroVonSelbstGesperrt`): Auto-Mikro, Wach-Wort, Freihand.
 * Martins Hand bleibt: die Sprechtaste geht auf und PAUSIERT die lange Spur (`pausiereFuerHand`); der Abspielknopf an einer
 * Blase ebenso.
 *
 * ⚠️ Kurze Töne (Quittung, Erinnerung, Klick) stehen nie auf der Bühne und zählen nicht. Eine Bühnen-Wiedergabe unter
 *    LANGE_DAUER_S (Dauer unbekannt = 0 zählt NICHT) ist keine lange Spur.
 * ⚠️ Nichts bleibt hängen: Pause/Ende/Fehler räumen die Bühne (ton-leitstand.ts) — danach gilt die Spur noch NACHLAUF_MS
 *    (ein Abschnittswechsel räumt die Bühne für Millisekunden). Rückt die Position einer „klingenden“ Spur HAENGT_MS lang
 *    nicht vor, gilt sie als hängend und gibt frei. Eine wartende Schlange gibt nach WARTE_MAX_MS auf.
 * ⚠️ Abbruch ist kein Ende: wer wartet, wird durch `cancel` (Signal) frei und sendet weder „Ende“ noch öffnet er das Mikro.
 * Notschalter: localStorage `voice-v8.lange-spur` = "aus" (Vorgabe: an).
 */
import { aktuellerTon, beobachte, halteAn, type TonStand } from "./ton-leitstand.js";
import { pausiereMusik, wiedergabeJetzt } from "./wiedergabe-stand.js";
import { meldeSpur } from "./mikro-spur.js";

export const LANGE_SPUR_SCHLUESSEL = "voice-v8.lange-spur";
/** Ab so vielen Sekunden Dauer ist eine Bühnen-Wiedergabe eine lange Spur. */
export const LANGE_DAUER_S = 60;
/** So lange gilt die Spur nach ihrem Ende/ihrer Pause weiter (Abschnittswechsel räumt die Bühne kurz). */
export const NACHLAUF_MS = 3000;
/** Rückt die Position so lange nicht vor, obwohl „klingt“, hängt die Spur. */
export const HAENGT_MS = 60_000;
/** Höchstens so lange wartet eine Schlange auf eine lange Spur. */
export const WARTE_MAX_MS = 45 * 60_000;
const WARTE_TAKT_MS = 250;
/** Takt der Hänge-Messung, solange eine lange Spur klingt. */
export const HAENGT_TAKT_MS = 5000;

/** Die Wege, auf denen das Mikro von selbst aufgeht (niemand hat gedrückt). */
const MIKRO_VON_SELBST: ReadonlySet<string> = new Set(["nach-frage", "nach-antwort", "wachwort", "freihand"]);

/** Ist die Regel an? Vorgabe an; nur der ausdrückliche Wert "aus" schaltet sie ab. */
export function langeSpurAn(): boolean {
  try {
    return globalThis.localStorage?.getItem(LANGE_SPUR_SCHLUESSEL) !== "aus";
  } catch {
    return true;
  }
}

/** Ist das, was die Bühne meldet, eine lange Spur? Rein. */
export function istLangeSpur(stand: TonStand | null): boolean {
  if (!stand?.klingt) return false;
  if (stand.art !== "spur" && stand.art !== "vorlesen" && stand.art !== "musik") return false;
  const dauer = stand.fortschritt?.dauer ?? 0;
  return Number.isFinite(dauer) && dauer > LANGE_DAUER_S;
}

let zuletzt = 0;
let warLang = false;

function standSicher(): TonStand | null {
  try {
    return aktuellerTon();
  } catch {
    return null;
  }
}

/**
 * Die Hänge-Messung (Prüfung 17.10.): hängt NICHT an Abfragen. Ein eigener Takt (`HAENGT_TAKT_MS`, nur solange eine lange
 * Spur auf der Bühne klingt) und jede Abfrage rufen `pruefeHaengt`; gemessen wird die ROHE Abspielzeit der Quelle
 * (`abspielzeit`), nicht die Position — die Engine zählt bei stehender Uhr die Wanduhr auf die Position. Sehr seltene
 * Abfragen ändern nichts: gemessen wird ab der letzten Änderung. Zusätzlich gilt eine Spur als zu Ende, wenn die Position
 * die Dauer deutlich überschreitet.
 */
let messung: { z: number; seit: number } = { z: NaN, seit: 0 };
let haengend = false;

function haengtMelden(art: string, grund: string): void {
  if (haengend) return;
  haengend = true;
  meldeSpur("lange-spur", `haengt art=${art} grund=${grund} -> frei`);
}

function pruefeHaengt(jetzt: number): void {
  const s = standSicher();
  if (!istLangeSpur(s) || !s) {
    messung = { z: NaN, seit: jetzt };
    haengend = false;
    return;
  }
  const pos = s.fortschritt?.position ?? NaN;
  const dauer = s.fortschritt?.dauer ?? 0;
  if (dauer > 0 && s.fortschritt?.geschaetzt !== true && pos > dauer * 1.5 + 30) return haengtMelden(s.art, "ueber-dauer");
  const z = s.abspielzeit ?? pos;
  if (z !== messung.z) {
    messung = { z, seit: jetzt };
    haengend = false;
  } else if (jetzt - messung.seit >= HAENGT_MS) {
    haengtMelden(s.art, `still-${Math.round((jetzt - messung.seit) / 1000)}s`);
  }
}

/** Die Art der laufenden langen Spur für das Journal — `null`, wenn keine läuft (ohne Nachlauf). */
function laufendeArt(jetzt: number): string | null {
  const s = standSicher();
  if (istLangeSpur(s)) {
    pruefeHaengt(jetzt);
    return haengend ? null : (s?.art ?? "spur");
  }
  try {
    return wiedergabeJetzt() === "musik" ? "musik" : null;
  } catch {
    return null;
  }
}

/** Läuft gerade eine lange Spur (oder ist sie vor weniger als NACHLAUF_MS verstummt)? */
export function langeSpurLaeuft(jetzt: number = Date.now()): boolean {
  if (!langeSpurAn()) return false;
  if (laufendeArt(jetzt) !== null) {
    zuletzt = jetzt;
    return true;
  }
  return zuletzt > 0 && jetzt - zuletzt < NACHLAUF_MS;
}

/**
 * Beginnt eine lange Spur: soll die gerade laufende Aufnahme verworfen werden? Nur wenn sie WIRKLICH aufnimmt
 * (`recording`) und von selbst aufging. „sending“ (der Zug läuft, er hat die Spur womöglich gestartet) und von Hand
 * geöffnete Aufnahmen bleiben. Rein.
 */
export function sollMikroVerwerfen(pttZustand: string, via: unknown): boolean {
  return pttZustand === "recording" && typeof via === "string" && ["nach-frage", "nach-antwort", "freihand"].includes(via);
}

/** Soll dieser Mikro-Anlass geschlossen bleiben? Nur die Wege „von selbst“; Martins Hand nie. */
export function mikroVonSelbstGesperrt(via: string, jetzt: number = Date.now()): boolean {
  if (!MIKRO_VON_SELBST.has(via)) return false;
  if (!langeSpurLaeuft(jetzt)) return false;
  meldeSpur("lange-spur", `mikro-zu via=${via} art=${standSicher()?.art ?? "musik"}`);
  return true;
}

/**
 * Martin greift von Hand ein (Sprechtaste, Abspielknopf, getippte Frage): eine laufende lange Spur wird PAUSIERT, nicht
 * überdeckt — die Bühne über ihr eigenes `anhalten`, eingebettete Musik per `pauseVideo` (wiedergabe-stand.ts). Danach
 * gilt sie sofort als frei (kein Nachlauf): Martins Antwort soll nicht warten. `nurMusik`: die getippte Frage pausiert Musik,
 * aber keine Meditation (deren Antwort wartet). true = etwas wurde pausiert.
 */
export function pausiereFuerHand(grund: string, nurMusik = false): boolean {
  if (!langeSpurAn()) return false;
  let tat = false;
  if (!nurMusik && istLangeSpur(standSicher())) {
    try {
      halteAn();
      tat = true;
    } catch {
      /* siehe unten: die Musik kann trotzdem pausiert werden */
    }
  }
  try {
    if (wiedergabeJetzt() === "musik" && pausiereMusik()) tat = true;
  } catch {
    /* Musik bleibt dann an, die Antwort wartet */
  }
  if (!tat) return false;
  zuletzt = 0;
  meldeSpur("lange-spur", `pausiert grund=${grund}`);
  return true;
}

export type LangeSpurWarten = "frei" | "abbruch" | "verworfen" | "frist";

/**
 * Wartet, bis keine lange Spur mehr läuft. `verwerfen`: nicht warten, sondern aufgeben (Schmied). Löst sofort auf, wenn
 * keine läuft. `signal` (cancel der Schlange) beendet das Warten mit "abbruch". Wirft nie.
 */
export function warteAufLangeSpur(
  signal: AbortSignal | undefined,
  opt: { verwerfen?: boolean; jetzt?: () => number } = {},
): Promise<LangeSpurWarten> {
  const jetzt = opt.jetzt ?? Date.now;
  if (signal?.aborted) return Promise.resolve("abbruch");
  if (!langeSpurLaeuft(jetzt())) return Promise.resolve("frei");
  if (opt.verwerfen) return Promise.resolve("verworfen");
  const start = jetzt();
  return new Promise((ok) => {
    const ende = (r: LangeSpurWarten): void => {
      clearInterval(takt);
      signal?.removeEventListener("abort", ab);
      ok(r);
    };
    const ab = (): void => ende("abbruch");
    const takt = setInterval(() => {
      const n = jetzt();
      if (!langeSpurLaeuft(n)) return ende("frei");
      if (n - start >= WARTE_MAX_MS) ende("frist");
    }, WARTE_TAKT_MS);
    (takt as unknown as { unref?: () => void }).unref?.();
    signal?.addEventListener("abort", ab, { once: true });
  });
}

let beobachtet = false;
let takt: ReturnType<typeof setInterval> | null = null;

/**
 * Einmal beim Start: hängt sich an die Bühne. Stempelt das Ende einer langen Spur (damit der Nachlauf ab dem Ende zählt,
 * nicht ab der letzten Prüfung) und meldet den BEGINN einer neuen (nicht bei einem Abschnittswechsel) an `beiBeginn` —
 * app.ts bricht dort die gerade sprechende Stimme ab, damit nichts überlagert.
 */
export function starteLangeSpur(beiBeginn?: (art: string) => void): void {
  if (beobachtet) return;
  beobachtet = true;
  beobachte((stand) => {
    const jetzt = Date.now();
    const lang = langeSpurAn() && istLangeSpur(stand);
    if (lang && !warLang) {
      const nachlauf = zuletzt > 0 && jetzt - zuletzt < NACHLAUF_MS;
      if (!nachlauf) {
        meldeSpur("lange-spur", `beginnt art=${stand?.art ?? "?"} dauer=${Math.round(stand?.fortschritt?.dauer ?? 0)}s`);
        try {
          beiBeginn?.(stand?.art ?? "spur");
        } catch {
          /* ein Rückruf darf die Bühne nie stören */
        }
      }
    }
    if (lang || warLang) zuletzt = jetzt;
    warLang = lang;
    // Jede Änderung der Bühne ist ein Lebenszeichen: die Messung beginnt neu.
    messung = { z: NaN, seit: jetzt };
    haengend = false;
    if (lang && !takt) {
      takt = setInterval(() => pruefeHaengt(Date.now()), HAENGT_TAKT_MS);
      (takt as unknown as { unref?: () => void }).unref?.();
    } else if (!lang && takt) {
      clearInterval(takt);
      takt = null;
    }
  });
}

/** Nur Tests. */
export function _langeSpurVergessen(): void {
  zuletzt = 0;
  warLang = false;
  messung = { z: NaN, seit: 0 };
  haengend = false;
  if (takt) clearInterval(takt);
  takt = null;
  beobachtet = false;
}

/** Der Text der Sprech-Pille, solange eine Antwort auf das Ende einer langen Spur wartet (17.10.2026). Kurz, ohne Inhalte. */
export const WARTET_AUF_LANGE_SPUR_TEXT = "Antwort wartet auf das Ende der Wiedergabe";
